import React, { useState, useEffect } from 'react';
import {
  Layout, Breadcrumb, Table, Tag, Space, Badge, Input, Button, Modal,
  Select, message, Statistic, Card, Row, Col, DatePicker, Typography,
  Popconfirm, Drawer, Form, InputNumber, Tooltip, Avatar, Timeline,
  Tabs, Alert, Progress, Divider
} from 'antd';
import {
  PhoneOutlined, MailOutlined, MessageOutlined, SearchOutlined,
  CalendarOutlined, TeamOutlined, CheckOutlined, ExclamationOutlined,
  UserOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  CrownOutlined, ClockCircleOutlined, DollarOutlined,
  FileTextOutlined, BellOutlined, FilterOutlined, DownloadOutlined,QuestionCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

const { Header, Content, Sider } = Layout;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const MembershipAdmin = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filteredMemberships, setFilteredMemberships] = useState([]);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    active: 0,
    expired: 0
  });
  const [form] = Form.useForm();
  const [noteForm] = Form.useForm();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    };
  };

  useEffect(() => {
    fetchMemberships();
  }, []);

  useEffect(() => {
    let filtered = [...memberships];

    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(membership =>
        membership.fullName.toLowerCase().includes(lower) ||
        membership.email.toLowerCase().includes(lower) ||
        membership.mobile.includes(searchText) ||
        (membership.membershipId && membership.membershipId.toLowerCase().includes(lower)) ||
        (membership.message && membership.message.toLowerCase().includes(lower))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(membership => membership.status === statusFilter);
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(membership => membership.plan === planFilter);
    }

    if (dateRange) {
      const [start, end] = dateRange;
      filtered = filtered.filter(membership => {
        const membershipDate = moment(membership.applicationDate);
        return membershipDate.isSameOrAfter(start, 'day') && membershipDate.isSameOrBefore(end, 'day');
      });
    }

    setFilteredMemberships(filtered);
  }, [memberships, searchText, statusFilter, planFilter, dateRange]);

  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/membership-applications`, getAuthHeader());
      setMemberships(res.data.data);
      setFilteredMemberships(res.data.data);
      setStatistics(res.data.statistics);
    } catch (err) {
      console.error('Error fetching memberships:', err);
      message.error('Failed to fetch membership applications');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status, additionalData = {}) => {
    try {
      await axios.put(
        `${backendUrl}/api/membership-applications/${id}`,
        { status, ...additionalData },
        getAuthHeader()
      );
      message.success(`Membership status updated to ${status}`);
      fetchMemberships();
      setIsDetailModalVisible(false);
      setIsEditDrawerVisible(false);
    } catch (err) {
      console.error('Update failed:', err);
      message.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${backendUrl}/api/membership-applications/${id}`, getAuthHeader());
      message.success('Membership application deleted');
      fetchMemberships();
    } catch (err) {
      console.error('Delete failed:', err);
      message.error('Failed to delete membership application');
    }
  };

  const handleBulkUpdate = async (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select membership applications to update');
      return;
    }

    try {
      await axios.put(
        `${backendUrl}/api/membership-applications/bulk/update`,
        { ids: selectedRowKeys, status },
        getAuthHeader()
      );
      message.success(`${selectedRowKeys.length} membership applications updated`);
      setSelectedRowKeys([]);
      fetchMemberships();
    } catch (err) {
      console.error('Bulk update failed:', err);
      message.error('Failed to update membership applications');
    }
  };

  const handleAddNote = async (values) => {
    try {
      await axios.put(
        `${backendUrl}/api/membership-applications/${currentMembership._id}`,
        { notes: values.note },
        getAuthHeader()
      );
      message.success('Note added successfully');
      noteForm.resetFields();
      fetchMemberships();
      // Refresh current membership data
      const res = await axios.get(
        `${backendUrl}/api/membership-applications/${currentMembership._id}`,
        getAuthHeader()
      );
      setCurrentMembership(res.data.data);
    } catch (err) {
      console.error('Failed to add note:', err);
      message.error('Failed to add note');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      active: 'blue',
      expired: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      approved: <CheckOutlined />,
      rejected: <ExclamationOutlined />,
      active: <CrownOutlined />,
      expired: <BellOutlined />
    };
    return icons[status] || <QuestionCircleOutlined />;
  };

  const getPlanColor = (plan) => {
    const colors = {
      '3-months': 'blue',
      '6-months': 'green',
      '1-year': 'gold'
    };
    return colors[plan] || 'default';
  };

  const calculateProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const now = moment();
    const start = moment(startDate);
    const end = moment(endDate);
    const total = end.diff(start);
    const elapsed = now.diff(start);
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };

  const columns = [
    {
      title: 'Member Info',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={40} style={{ backgroundColor: '#ff814a' }}>
            {record.fullName.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{record.fullName}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.mobile}
            </div>
          </div>
        </div>
      ),
      width: 250,
    },
    {
      title: 'Plan & ID',
      render: (_, record) => (
        <div>
          <Tag color={getPlanColor(record.plan)} style={{ marginBottom: 4 }}>
            {record.plan.toUpperCase()}
          </Tag>
          <br />
          {record.membershipId && (
            <Text code style={{ fontSize: '11px' }}>{record.membershipId}</Text>
          )}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status, record) => (
        <div>
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
            {status.toUpperCase()}
          </Tag>
          {record.paymentStatus && (
            <div style={{ marginTop: 4 }}>
              <Tag size="small" color={record.paymentStatus === 'completed' ? 'success' : 'warning'}>
                {record.paymentStatus}
              </Tag>
            </div>
          )}
        </div>
      ),
      width: 120,
    },
    {
      title: 'Timeline',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div><strong>Applied:</strong> {moment(record.applicationDate).format('MMM DD, YYYY')}</div>
          {record.approvedDate && (
            <div><strong>Approved:</strong> {moment(record.approvedDate).format('MMM DD, YYYY')}</div>
          )}
          {record.startDate && record.endDate && (
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={calculateProgress(record.startDate, record.endDate)}
                size="small"
                showInfo={false}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span>{moment(record.startDate).format('MMM DD')}</span>
                <span>{moment(record.endDate).format('MMM DD')}</span>
              </div>
            </div>
          )}
        </div>
      ),
      width: 180,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: amount => amount ? `$${amount}` : '-',
      width: 80,
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="primary" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => showDetailModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => showEditDrawer(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this membership application?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button 
                danger 
                size="small" 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      width: 140,
    },
  ];

  const showDetailModal = async (membership) => {
    try {
      const res = await axios.get(
        `${backendUrl}/api/membership-applications/${membership._id}`,
        getAuthHeader()
      );
      setCurrentMembership(res.data.data);
      setIsDetailModalVisible(true);
    } catch (err) {
      console.error('Error fetching membership details:', err);
      message.error('Failed to fetch membership details');
    }
  };

  const showEditDrawer = (membership) => {
    setCurrentMembership(membership);
    form.setFieldsValue({
      status: membership.status,
      amount: membership.amount,
      startDate: membership.startDate ? moment(membership.startDate) : null,
      endDate: membership.endDate ? moment(membership.endDate) : null,
      paymentStatus: membership.paymentStatus
    });
    setIsEditDrawerVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      const updateData = {
        status: values.status,
        amount: values.amount,
        paymentStatus: values.paymentStatus,
        startDate: values.startDate?.toISOString(),
        endDate: values.endDate?.toISOString(),
        notes: values.notes
      };

      await axios.put(
        `${backendUrl}/api/membership-applications/${currentMembership._id}`,
        updateData,
        getAuthHeader()
      );
      
      message.success('Membership updated successfully');
      setIsEditDrawerVisible(false);
      fetchMemberships();
    } catch (err) {
      console.error('Update failed:', err);
      message.error('Failed to update membership');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            <CrownOutlined style={{ marginRight: 8 }} />
            Membership Management
          </Title>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
          >
            Export Data
          </Button>
        </div>
      </Header>

      <Content style={{ margin: '24px' }}>
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>Admin</Breadcrumb.Item>
          <Breadcrumb.Item>Membership Applications</Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistics Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Total Applications"
                value={statistics.total}
                prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Pending"
                value={statistics.pending}
                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Approved"
                value={statistics.approved}
                prefix={<CheckOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Active"
                value={statistics.active}
                prefix={<CrownOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Rejected"
                value={statistics.rejected}
                prefix={<ExclamationOutlined style={{ color: '#f5222d' }} />}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card>
              <Statistic
                title="Expired"
                value={statistics.expired}
                prefix={<BellOutlined style={{ color: '#8c8c8c' }} />}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters and Actions */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Search
                placeholder="Search by name, email, phone, or ID"
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={value => setSearchText(value)}
                onChange={e => setSearchText(e.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select 
                style={{ width: '100%' }} 
                defaultValue="all" 
                onChange={value => setStatusFilter(value)}
                placeholder="Status"
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="active">Active</Option>
                <Option value="expired">Expired</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select 
                style={{ width: '100%' }} 
                defaultValue="all" 
                onChange={value => setPlanFilter(value)}
                placeholder="Plan"
              >
                <Option value="all">All Plans</Option>
                <Option value="3-months">3 Months</Option>
                <Option value="6-months">6 Months</Option>
                <Option value="1-year">1 Year</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker 
                style={{ width: '100%' }} 
                onChange={setDateRange}
                placeholder={['Start Date', 'End Date']}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button 
                icon={<SearchOutlined />} 
                onClick={fetchMemberships} 
                type="primary" 
                style={{ width: '100%'}}
              >
                Refresh
              </Button>
            </Col>
          </Row>

          {/* Bulk Actions */}
          {selectedRowKeys.length > 0 && (
            <div style={{ marginTop: 16, padding: 16, background: '#f0f2f5', borderRadius: 8 }}>
              <Text strong>{selectedRowKeys.length} items selected</Text>
              <Space style={{ marginLeft: 16 }}>
                <Button size="small" onClick={() => handleBulkUpdate('approved')}>
                  Approve Selected
                </Button>
                <Button size="small" onClick={() => handleBulkUpdate('rejected')}>
                  Reject Selected
                </Button>
                <Button size="small" onClick={() => handleBulkUpdate('active')}>
                  Activate Selected
                </Button>
                <Button size="small" danger onClick={() => setSelectedRowKeys([])}>
                  Clear Selection
                </Button>
              </Space>
            </div>
          )}
        </Card>

        {/* Data Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredMemberships}
            rowKey="_id"
            loading={loading}
            rowSelection={rowSelection}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </Content>

      {/* Detail Modal */}
      {currentMembership && (
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar size={40} style={{ backgroundColor: '#ff814a' }}>
                {currentMembership.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {currentMembership.fullName}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {currentMembership.membershipId || 'No ID assigned'}
                </div>
              </div>
            </div>
          }
          open={isDetailModalVisible}
          onCancel={() => setIsDetailModalVisible(false)}
          width={800}
          footer={[
            <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                setIsDetailModalVisible(false);
                showEditDrawer(currentMembership);
              }}
              style={{ backgroundColor: '#ff814a', borderColor: '#ff814a' }}
            >
              Edit Membership
            </Button>,
          ]}
        >
          <Tabs defaultActiveKey="details">
            <TabPane tab="Details" key="details">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div>
                    <Text strong>Contact Information</Text>
                    <div style={{ marginTop: 8 }}>
                      <p><UserOutlined /> <strong>Name:</strong> {currentMembership.fullName}</p>
                      <p><MailOutlined /> <strong>Email:</strong> {currentMembership.email}</p>
                      <p><PhoneOutlined /> <strong>Mobile:</strong> {currentMembership.mobile}</p>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Text strong>Membership Details</Text>
                    <div style={{ marginTop: 8 }}>
                      <p><strong>Plan:</strong> 
                        <Tag color={getPlanColor(currentMembership.plan)} style={{ marginLeft: 8 }}>
                          {currentMembership.plan}
                        </Tag>
                      </p>
                      <p><strong>Status:</strong> 
                        <Tag color={getStatusColor(currentMembership.status)} style={{ marginLeft: 8 }}>
                          {currentMembership.status}
                        </Tag>
                      </p>
                      <p><strong>Amount:</strong> {currentMembership.amount ? `${currentMembership.amount}` : 'Not set'}</p>
                    </div>
                  </div>
                </Col>
              </Row>

              <Divider />

              <div>
                <Text strong>Timeline</Text>
                <div style={{ marginTop: 16 }}>
                  <Timeline>
                    <Timeline.Item color="blue">
                      <p><strong>Application Submitted</strong></p>
                      <p>{moment(currentMembership.applicationDate).format('MMMM DD, YYYY - h:mm A')}</p>
                    </Timeline.Item>
                    {currentMembership.approvedDate && (
                      <Timeline.Item color="green">
                        <p><strong>Application Approved</strong></p>
                        <p>{moment(currentMembership.approvedDate).format('MMMM DD, YYYY - h:mm A')}</p>
                      </Timeline.Item>
                    )}
                    {currentMembership.startDate && (
                      <Timeline.Item color="purple">
                        <p><strong>Membership Started</strong></p>
                        <p>{moment(currentMembership.startDate).format('MMMM DD, YYYY')}</p>
                      </Timeline.Item>
                    )}
                    {currentMembership.endDate && (
                      <Timeline.Item color={moment().isAfter(currentMembership.endDate) ? 'red' : 'orange'}>
                        <p><strong>Membership {moment().isAfter(currentMembership.endDate) ? 'Expired' : 'Expires'}</strong></p>
                        <p>{moment(currentMembership.endDate).format('MMMM DD, YYYY')}</p>
                      </Timeline.Item>
                    )}
                  </Timeline>
                </div>
              </div>

              {currentMembership.message && (
                <>
                  <Divider />
                  <div>
                    <Text strong><MessageOutlined /> Message:</Text>
                    <div style={{ 
                      background: '#f5f5f5', 
                      padding: 15, 
                      borderRadius: 8, 
                      marginTop: 8,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {currentMembership.message}
                    </div>
                  </div>
                </>
              )}
            </TabPane>

            <TabPane tab="Notes & History" key="notes">
              <div style={{ marginBottom: 16 }}>
                <Text strong>Add Note</Text>
                <Form form={noteForm} onFinish={handleAddNote} style={{ marginTop: 8 }}>
                  <Form.Item
                    name="note"
                    rules={[{ required: true, message: 'Please enter a note' }]}
                  >
                    <TextArea
                      rows={3}
                      placeholder="Add a note about this membership..."
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button 
                      type="primary" 
                      htmlType="submit"
                      style={{ backgroundColor: '#ff814a', borderColor: '#ff814a' }}
                    >
                      Add Note
                    </Button>
                  </Form.Item>
                </Form>
              </div>

              <Divider />

              <div>
                <Text strong>Notes History</Text>
                <div style={{ marginTop: 16 }}>
                  {currentMembership.notes && currentMembership.notes.length > 0 ? (
                    <Timeline>
                      {currentMembership.notes.map((note, index) => (
                        <Timeline.Item key={index}>
                          <div>
                            <Text strong>{note.addedBy}</Text>
                            <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                              {moment(note.addedAt).format('MMM DD, YYYY - h:mm A')}
                            </Text>
                          </div>
                          <div style={{ marginTop: 4 }}>{note.content}</div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  ) : (
                    <Alert
                      message="No notes available"
                      description="No notes have been added to this membership yet."
                      type="info"
                      showIcon
                    />
                  )}
                </div>
              </div>
            </TabPane>
          </Tabs>
        </Modal>
      )}

      {/* Edit Drawer */}
      <Drawer
        title="Edit Membership"
        width={600}
        onClose={() => setIsEditDrawerVisible(false)}
        open={isEditDrawerVisible}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setIsEditDrawerVisible(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={() => form.submit()}
              style={{ backgroundColor: '#ff814a', borderColor: '#ff814a' }}
            >
              Update Membership
            </Button>
          </div>
        }
      >
        {currentMembership && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditSubmit}
          >
            <Alert
              message={`Editing: ${currentMembership.fullName}`}
              type="info"
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="status"
              label="Membership Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="active">Active</Option>
                <Option value="expired">Expired</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="paymentStatus"
              label="Payment Status"
            >
              <Select>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="refunded">Refunded</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Membership Amount ($)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="Enter amount"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label="End Date"
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label="Add Note (Optional)"
            >
              <TextArea
                rows={4}
                placeholder="Add a note about this update..."
              />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </Layout>
  );
};

export default MembershipAdmin;